import { Component, inject, input, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { Store } from '@ngxs/store';
import { DmButton, DmCombobox, DmSelectItem } from '@digitale-menschen/component-library-angular';
import {
  ImpersonationState,
  ImpersonationStateClear,
  ImpersonationStateSet,
  ProfileState,
  ProfileStateSetKundeDokumentenManagerAktiv,
} from '@lexplus-shared-lib';
import {
  impersonationControllerSearchV1,
  impersonationControllerStartV1,
  impersonationControllerStopV1,
  LexplusDropdownMiniDto,
} from '@lexplus-user-api';
import { API_CONFIGURATION } from '@lexplus-environments';

/**
 * Sidebar footer control that lets a Neosys admin search for a Kunde and impersonate
 * them. Visible to Neosys admins only. While an impersonation is active it shows a
 * banner with the current Kunde and a stop button instead. The impersonated Kunde is
 * held in `ImpersonationState`; requests carry the header via `ApiTokenInterceptor`.
 */
@Component({
  selector: 'lp-impersonation-selector',
  imports: [DmButton, DmCombobox, DmSelectItem],
  templateUrl: './impersonation-selector.component.html',
  styleUrl: './impersonation-selector.component.scss',
  host: {
    '[class.lp-impersonation-selector--collapsed]': 'collapsed()',
  },
})
export class ImpersonationSelectorComponent {
  public readonly collapsed = input<boolean>(false);

  private readonly store = inject(Store);
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfiguration = inject(API_CONFIGURATION);

  protected readonly isNeosysAdmin = this.store.selectSignal(ProfileState.isAdmin);
  protected readonly isImpersonating = this.store.selectSignal(ImpersonationState.isImpersonating);
  protected readonly impersonatedKundeName = this.store.selectSignal(ImpersonationState.kundeName);

  protected readonly kunden = signal<LexplusDropdownMiniDto[]>([]);
  protected readonly loading = signal<boolean>(false);

  private isStarting = false;

  /** Minimum number of characters that triggers the Kunde search. */
  private readonly minSearchLength = 3;

  /**
   * Searches Kunden server-side from the entered search term. The search only triggers
   * from {@link minSearchLength} characters up; below that the result list is cleared
   * and no request is sent.
   *
   * @param query Search term emitted by the combobox.
   */
  protected async searchKunden(query: string): Promise<void> {
    const trimmed = query.trim();
    if (trimmed.length < this.minSearchLength) {
      this.kunden.set([]);
      return;
    }
    this.loading.set(true);
    try {
      const response = await lastValueFrom(
        impersonationControllerSearchV1(this.httpClient, this.apiConfiguration.baseUrlUser, { query: trimmed }),
      );
      this.kunden.set(response.body);
    } catch {
      this.kunden.set([]);
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Starts impersonating the selected Kunde: logs it server-side, sets the state and
   * reloads the app so all data is fetched again in the Kunde's context.
   *
   * @param value The value delivered by the combobox (KundeID as a string).
   */
  protected async startImpersonation(value: string): Promise<void> {
    const kundeID = Number.parseInt(value, 10);
    if (this.isStarting || Number.isNaN(kundeID)) {
      return;
    }
    this.isStarting = true;
    const kundeName = this.kunden().find((kunde) => kunde.intId === kundeID)?.label ?? '';
    try {
      await lastValueFrom(
        impersonationControllerStartV1(this.httpClient, this.apiConfiguration.baseUrlUser, {
          body: { kundeID },
        }),
      );
      this.store.dispatch(new ImpersonationStateSet(kundeID, kundeName));
      this.resetKundeSwitches();
      this.reloadPage();
    } catch {
      this.isStarting = false;
    }
  }

  /**
   * Ends the impersonation: closes the log entry, clears the state and reloads the app.
   */
  protected async stopImpersonation(): Promise<void> {
    try {
      await lastValueFrom(impersonationControllerStopV1(this.httpClient, this.apiConfiguration.baseUrlUser, {}));
    } finally {
      this.store.dispatch(new ImpersonationStateClear());
      this.resetKundeSwitches();
      this.reloadPage();
    }
  }

  /**
   * Closes the Kunde's module switches until the reload has read the new Kunde's: the profile is
   * persisted, so the previous Kunde's switches would otherwise decide if that read fails.
   */
  private resetKundeSwitches(): void {
    this.store.dispatch(new ProfileStateSetKundeDokumentenManagerAktiv(false));
  }

  /**
   * Reloads the page. Wrapped in its own method to keep it testable.
   */
  private reloadPage(): void {
    window.location.reload();
  }
}
